import React, { useState } from "react";
import {
    CommandBarButton,
    CommandBarButtonMenuItem,
    ConfirmDialog,
    depascalize,
    useDisclosure,
    useNotifications
} from "sprint-common-web";

import { AuthorizationWrapper } from "../../../../core/security/AuthorizationWrapper";
import { SprintAccessRight } from "../../../../core/security/SprintAccessRight";
import { OnPremOrderStatus } from "../../../common/types/OnPremOrderStatus";
import { useOrdersModuleApi } from "../../../common/useOrdersModuleApi";
import {
    allowedOnPremOrderStatuses,
    getAvailableOnPremOrderStatuses,
    mapOnPremOrderStatusName
} from "../../common/utils/getAvailableOnPremOrderStatuses";
import { mapOrderStatusIcon } from "../../common/utils/mapOrderStatusIcon";
import { useGetOnPremOrderDashboard } from "../hooks/useGetOnPremOrderDashboard";

export type UpdateOnPremOrderStatusDetailsButtonProps = {
    status: OnPremOrderStatus;
};

export const UpdateOnPremOrderStatusDetailsButton: React.VFC = () => {
    const { onPremOrderDetails, updateOnPremOrderDetails } = useGetOnPremOrderDashboard();
    const { updateOnPremOrderStatus } = useOrdersModuleApi();
    const { isOpen, onClose, onOpen } = useDisclosure();

    const [selectedStatus, setSelectedStatus] = useState<OnPremOrderStatus>();
    const { success } = useNotifications();

    const availableStatuses = getAvailableOnPremOrderStatuses(onPremOrderDetails.status);

    const statusOptions: Array<CommandBarButtonMenuItem & { key: OnPremOrderStatus }> = allowedOnPremOrderStatuses.map(
        (status) => ({
            key: status,
            text: mapOnPremOrderStatusName(status),
            icon: mapOrderStatusIcon[status],
            disabled: !availableStatuses.includes(status),
            onClick: () => {
                setSelectedStatus(Number(status));
                onOpen();
            }
        })
    );

    const updateStatus = async () => {
        if (!selectedStatus) return;

        await updateOnPremOrderStatus({ orderId: onPremOrderDetails.id, status: selectedStatus });

        success("On-Prem order status updated");

        updateOnPremOrderDetails({ status: selectedStatus });
        onClose();
    };

    const allowedStatusForCurrStatus = getAvailableOnPremOrderStatuses(onPremOrderDetails.status);

    return (
        <AuthorizationWrapper accessRights={[SprintAccessRight.OrdReadWrite]}>
            <React.Fragment key={`${onPremOrderDetails.id}-update-on-prem-order-status-details-button`}>
                <CommandBarButton
                    text={`Status: ${mapOnPremOrderStatusName(onPremOrderDetails.status) || "..."}`}
                    icon={mapOrderStatusIcon[onPremOrderDetails.status]}
                    menuItems={statusOptions.map((button) => ({
                        text: button.text,
                        icon: button.icon,
                        disabled: !allowedStatusForCurrStatus.includes(button.key),
                        onClick: button.onClick
                    }))}
                    disabled={availableStatuses.length === 0}
                    testId="on-prem-order-status-button"
                />
                {!!selectedStatus && (
                    <ConfirmDialog
                        title="Update On-Prem order status"
                        text={`Are you sure you want to update On-Prem order status to ${depascalize(
                            OnPremOrderStatus[selectedStatus]
                        )}`}
                        hidden={!isOpen}
                        confirm={{ onClick: updateStatus }}
                        cancel={{ onClick: onClose }}
                    />
                )}
            </React.Fragment>
        </AuthorizationWrapper>
    );
};
